// Lead-Tabelle für Smart Views und die Lead-Liste: Spaltenköpfe mit Sortierung, Tastatur (j/k, Enter),
// Fokuszeile, Vorladen beim Überfahren, Nachladen am Ende. Spaltenordnung (Name, Status, Telefon/E-Mail als Symbole,
// Rest der View) kommt aus listenSpalten.ts. Markieren: Wer `auswahl` übergibt und eine Spalte mit dem Schlüssel
// `AUSWAHL_SPALTE` führt, bekommt die Kästchen von der Tabelle selbst: eins je Zeile (Umschalt-Klick markiert einen
// Bereich; Taste x markiert die Fokuszeile, Esc hebt die Markierung auf) und im Kopf „alle geladenen markieren“ mit Zwischenzustand.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { LeadZeile } from "../api";
import { vorladenAbbrechen, vorladenVerzoegert } from "../daten/leadCache";
import { useTasten } from "../werkzeuge/tasten";
import { Icon } from "./Icon";

export interface Spalte {
  key: string;
  label: string;
  sortierbar?: boolean;
  klasse?: string;
  /** Tooltip und Vorlesetext für Spalten ohne sichtbare Überschrift (z. B. die Symbolspalte Telefon/E-Mail) */
  kopfTitel?: string;
}

/** Schlüssel der Spalte mit den Markierungskästchen. */
export const AUSWAHL_SPALTE = "__auswahl";

export interface Auswahl {
  markiert: ReadonlySet<string>;
  onAendern: (neu: Set<string>) => void;
}

export interface Sortierung {
  key: string;
  ab: boolean;
}

interface Props {
  spalten: Spalte[];
  zeilen: LeadZeile[];
  zelle: (z: LeadZeile, s: Spalte) => ReactNode;
  fokus: number;
  setFokus: (i: number) => void;
  onOeffnen: (z: LeadZeile, i: number) => void;
  sort?: Sortierung | null;
  onSort?: (key: string) => void;
  /** wird aufgerufen, wenn das Tabellenende sichtbar wird */
  onEnde?: () => void;
  fuss?: ReactNode;
  /** zusätzliche Klasse an der Tabelle, z. B. „tabelle--dicht“ bei vielen Spalten */
  klasse?: string;
  /** Markierung der Zeilen (siehe Kopf der Datei) */
  auswahl?: Auswahl;
  /** Spaltenbreiten in px je Spaltenschlüssel (je Nutzer gespeichert); ohne Eintrag: Breite nach Inhalt */
  breiten?: Record<string, number>;
  /** Breite gezogen (px) oder per Doppelklick auf den Griff zurückgesetzt (null) */
  onBreite?: (key: string, px: number | null) => void;
  /** Spalte per Ziehen im Kopf verschoben: `von` steht danach vor `nach` */
  onVerschieben?: (von: string, nach: string) => void;
  /** Berechnungszeile unter der Tabelle (Runde 10, Agent 10B): Inhalt der Zelle je Spalte; die Zeile bleibt am unteren Rand stehen */
  berechnung?: (s: Spalte) => ReactNode;
}

const MIN_BREITE = 64;
const MAX_BREITE = 640;

export function LeadTabelle({ spalten, zeilen, zelle, fokus, setFokus, onOeffnen, sort, onSort, onEnde, fuss, klasse, auswahl, breiten, onBreite, onVerschieben, berechnung }: Props) {
  const koerper = useRef<HTMLTableSectionElement>(null);
  const ende = useRef<HTMLDivElement>(null);
  const endeRef = useRef(onEnde);
  endeRef.current = onEnde;
  const letzterKlick = useRef<number | null>(null);
  const [live, setLive] = useState<{ key: string; px: number } | null>(null);
  const [zieht, setZieht] = useState<string | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);
  // erste Datenspalte (Name) bleibt beim seitlichen Scrollen stehen, die Markierungsspalte davor ebenfalls
  const nameKey = spalten.find((s) => s.key !== AUSWAHL_SPALTE)?.key;
  const breiteVon = (key: string): number | undefined => (live?.key === key ? live.px : breiten?.[key]);
  const zellStil = (key: string): CSSProperties | undefined => {
    const b = breiteVon(key);
    return b ? { width: b, minWidth: b, maxWidth: b } : undefined;
  };
  const ziehen = (e: React.PointerEvent<HTMLSpanElement>, key: string) => {
    if (!onBreite) return;
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget.closest("th") as HTMLElement | null) ?? e.currentTarget;
    const start = th.getBoundingClientRect().width;
    const x0 = e.clientX;
    let px = start;
    const zeiger = e.currentTarget;
    zeiger.setPointerCapture(e.pointerId);
    const bewegt = (m: PointerEvent) => {
      px = Math.round(Math.max(MIN_BREITE, Math.min(MAX_BREITE, start + m.clientX - x0)));
      setLive({ key, px });
    };
    const fertig = () => {
      zeiger.removeEventListener("pointermove", bewegt);
      zeiger.removeEventListener("pointerup", fertig);
      zeiger.removeEventListener("pointercancel", fertig);
      setLive(null);
      if (px !== Math.round(start)) onBreite(key, px);
    };
    zeiger.addEventListener("pointermove", bewegt);
    zeiger.addEventListener("pointerup", fertig);
    zeiger.addEventListener("pointercancel", fertig);
  };

  const alleAn = !!auswahl && zeilen.length > 0 && zeilen.every((z) => auswahl.markiert.has(z.id));
  const einigeAn = !!auswahl && !alleAn && zeilen.some((z) => auswahl.markiert.has(z.id));

  const alleUmschalten = () => {
    if (!auswahl) return;
    const neu = new Set(auswahl.markiert);
    for (const z of zeilen) (alleAn ? neu.delete(z.id) : neu.add(z.id));
    auswahl.onAendern(neu);
  };

  const zeileUmschalten = (i: number, bereich: boolean) => {
    if (!auswahl) return;
    const neu = new Set(auswahl.markiert);
    const an = !neu.has(zeilen[i].id);
    const von = bereich && letzterKlick.current !== null && letzterKlick.current < zeilen.length ? Math.min(letzterKlick.current, i) : i;
    const bis = bereich && letzterKlick.current !== null && letzterKlick.current < zeilen.length ? Math.max(letzterKlick.current, i) : i;
    for (let k = von; k <= bis; k++) (an ? neu.add(zeilen[k].id) : neu.delete(zeilen[k].id));
    letzterKlick.current = i;
    auswahl.onAendern(neu);
  };

  useEffect(() => {
    const zeile = koerper.current?.querySelector<HTMLElement>(`tr[data-index="${fokus}"]`);
    zeile?.scrollIntoView({ block: "nearest" });
  }, [fokus]);

  useEffect(() => {
    const el = ende.current;
    if (!el) return;
    const beobachter = new IntersectionObserver(
      (e) => {
        if (e.some((x) => x.isIntersecting)) endeRef.current?.();
      },
      { rootMargin: "300px" },
    );
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);

  useTasten({
    j: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
    ArrowDown: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
    k: () => setFokus(Math.max(fokus - 1, 0)),
    ArrowUp: () => setFokus(Math.max(fokus - 1, 0)),
    Enter: () => {
      const z = zeilen[fokus];
      if (z) onOeffnen(z, fokus);
    },
    // Runde 5 (Agent 5B): x markiert die Fokuszeile, Esc hebt die Markierung auf (nur mit Auswahl; Esc nur, wenn etwas markiert ist)
    ...(auswahl && zeilen[fokus] ? { x: () => zeileUmschalten(fokus, false) } : {}),
    ...(auswahl && auswahl.markiert.size > 0 ? { Escape: () => auswahl.onAendern(new Set()) } : {}),
  });

  useEffect(() => {
    const z = zeilen[fokus];
    if (z) vorladenVerzoegert(z.id);
  }, [fokus, zeilen]);

  return (
    <div className="tabelle-rahmen">
      <table className={`tabelle tabelle--leads${auswahl ? " tabelle--mit-auswahl" : ""}${klasse ? ` ${klasse}` : ""}`}>
        <thead>
          <tr>
            {spalten.map((s) => {
              if (auswahl && s.key === AUSWAHL_SPALTE) {
                return (
                  <th key={s.key} className={`${s.klasse ?? ""} tabelle__fix0`}>
                    <input
                      type="checkbox"
                      checked={alleAn}
                      ref={(el) => {
                        if (el) el.indeterminate = einigeAn;
                      }}
                      title="Alle geladenen Leads markieren"
                      aria-label="Alle geladenen Leads markieren"
                      disabled={!zeilen.length}
                      onChange={alleUmschalten}
                    />
                  </th>
                );
              }
              const aktiv = sort?.key === s.key;
              const beweglich = !!onVerschieben && s.key !== nameKey;
              return (
                <th
                  key={s.key}
                  className={`${s.klasse ?? ""}${s.key === nameKey ? " tabelle__fix" : ""}${ziel === s.key && zieht !== s.key ? " tabelle__ziel" : ""}${zieht === s.key ? " tabelle__zieht" : ""}`}
                  style={zellStil(s.key)}
                  aria-sort={aktiv ? (sort?.ab ? "descending" : "ascending") : undefined}
                  draggable={beweglich}
                  onDragStart={(e) => {
                    if (!beweglich) return;
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", s.key);
                    setZieht(s.key);
                  }}
                  onDragOver={(e) => {
                    if (zieht && s.key !== nameKey) {
                      e.preventDefault();
                      setZiel(s.key);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (zieht && onVerschieben && s.key !== nameKey) onVerschieben(zieht, s.key);
                    setZieht(null);
                    setZiel(null);
                  }}
                  onDragEnd={() => {
                    setZieht(null);
                    setZiel(null);
                  }}
                >
                  {s.sortierbar && onSort ? (
                    <button type="button" className={`tabelle__sort${aktiv ? " tabelle__sort--an" : ""}`} onClick={() => onSort(s.key)} title={`${s.label}: sortieren`}>
                      <span className="tabelle__sort-text">{s.label}</span>
                      {aktiv ? <Icon name={sort?.ab ? "pfeil-runter" : "pfeil-hoch"} groesse={13} /> : <Icon name="sortieren" groesse={13} className="tabelle__sort-icon" />}
                    </button>
                  ) : (
                    <span className="tabelle__kopf" title={s.kopfTitel ?? s.label}>
                      {s.label || <span className="nur-vorlesen">{s.kopfTitel}</span>}
                    </span>
                  )}
                  {onBreite ? (
                    <span
                      className="tabelle__griff"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Breite von ${s.label || "Spalte"} ändern`}
                      title="Breite ziehen, Doppelklick: zurücksetzen"
                      draggable={false}
                      onPointerDown={(e) => ziehen(e, s.key)}
                      onDoubleClick={(e) => (e.stopPropagation(), onBreite(s.key, null))}
                    />
                  ) : null}
                </th>
              );
            })}
            {/* Restbreite: die Spalten bleiben so schmal wie ihr Inhalt, Status steht direkt neben dem Namen */}
            <th className="tabelle__rest" aria-hidden="true" />
          </tr>
        </thead>
        <tbody ref={koerper}>
          {zeilen.map((z, i) => (
            <tr
              key={z.id}
              data-index={i}
              className={i === fokus ? "tabelle__zeile--fokus" : undefined}
              aria-selected={i === fokus}
              onMouseEnter={() => vorladenVerzoegert(z.id)}
              onMouseLeave={vorladenAbbrechen}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("button, a, input")) return;
                setFokus(i);
                onOeffnen(z, i);
              }}
            >
              {spalten.map((s) =>
                auswahl && s.key === AUSWAHL_SPALTE ? (
                  <td key={s.key} className={`${s.klasse ?? ""} tabelle__fix0`}>
                    <input
                      type="checkbox"
                      checked={auswahl.markiert.has(z.id)}
                      aria-label={`${z.display_name ?? z.name ?? "Lead"} markieren`}
                      onChange={() => undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        zeileUmschalten(i, e.shiftKey);
                      }}
                    />
                  </td>
                ) : (
                  <td key={s.key} className={`${s.klasse ?? ""}${s.key === nameKey ? " tabelle__fix" : ""}`} style={zellStil(s.key)}>
                    <div className="zelle" style={breiteVon(s.key) ? { maxWidth: "none" } : undefined}>{zelle(z, s)}</div>
                  </td>
                ),
              )}
              <td className="tabelle__rest" aria-hidden="true" />
            </tr>
          ))}
        </tbody>
        {berechnung ? (
          <tfoot className="tabelle__berechnung">
            <tr>
              {spalten.map((s) => (
                <td
                  key={s.key}
                  className={`${s.klasse ?? ""}${auswahl && s.key === AUSWAHL_SPALTE ? " tabelle__fix0" : s.key === nameKey ? " tabelle__fix" : ""}`}
                  style={auswahl && s.key === AUSWAHL_SPALTE ? undefined : zellStil(s.key)}
                >
                  {s.key === AUSWAHL_SPALTE ? null : berechnung(s)}
                </td>
              ))}
              <td className="tabelle__rest" aria-hidden="true" />
            </tr>
          </tfoot>
        ) : null}
      </table>
      <div ref={ende} className="tabelle__ende" />
      {fuss}
    </div>
  );
}
